import { useEffect, useState, useCallback } from "react";
import { Loader2, History } from "lucide-react";
import api from "../lib/apiClient";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join("+");
  if (typeof v === "object") return Object.entries(v).map(([k, x]) => `${k}: ${x}`).join(", ");
  if (typeof v === "boolean") return v ? "oui" : "non";
  return String(v);
};

const dt = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

const ACTION_COLOR = {
  PLAN_CHANGE: "text-amber-400 border-amber-500/40",
  STATUS_CHANGE: "text-red-400 border-red-500/40",
  ROLES_CHANGE: "text-emerald-400 border-emerald-500/40",
  PLAN_CONFIG: "text-sky-400 border-sky-500/40",
  IMAGE_DELETE: "text-zinc-300 border-zinc-600",
};

export default function AdminAudit() {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("");
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ action, page: String(page) });
    try { setData((await api.get(`/admin/audit?${p}`)).data); } catch {}
  }, [action, page]);
  useEffect(() => { load(); }, [load]);

  const actions = data?.actions || {};

  return (
    <div className="space-y-5" data-testid="admin-audit">
      <div className="flex items-center gap-2">
        <History className="w-6 h-6 text-amber-400" />
        <h1 className="font-display text-3xl font-extrabold">Historique</h1>
      </div>
      <p className="text-sm text-zinc-500">Journal des actions d'administration (offres, statuts, rôles, modération).</p>

      <div className="flex flex-wrap gap-2">
        {[["", "Toutes"], ...Object.entries(actions)].map(([k, label]) => (
          <button key={k || "all"} onClick={() => { setPage(0); setAction(k); }} data-testid={`audit-filter-${k || "all"}`}
            className={`px-3 h-10 rounded-xl text-sm border ${action === k ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{label}</button>
        ))}
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div> : (
        <>
          {data.logs.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 text-sm" data-testid="audit-empty">Aucune action enregistrée pour ce filtre.</div>
          ) : (
            <div className="space-y-2">
              {data.logs.map((l) => (
                <div key={l.id} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`audit-row-${l.id}`}>
                  <span className={`shrink-0 h-7 px-3 inline-flex items-center rounded-lg text-xs border ${ACTION_COLOR[l.action] || "text-zinc-300 border-zinc-600"}`}>{l.action_label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200 truncate">
                      <span className="text-zinc-400">Par</span> {l.actor_email}
                      {l.target_email && <> · <span className="text-zinc-400">sur</span> {l.target_name || l.target_email} <span className="text-zinc-600">({l.target_email})</span></>}
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {(l.before !== null && l.before !== undefined) || (l.after !== null && l.after !== undefined)
                        ? <>{fmt(l.before)} <span className="text-zinc-600">→</span> {fmt(l.after)}</> : null}
                      {l.detail && <> · {l.detail}</>}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-zinc-500 font-mono">{dt(l.created_at)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between pt-2">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-9 px-4 rounded-lg text-sm bg-zinc-900 border border-zinc-800 disabled:opacity-40">Précédent</button>
            <button disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} data-testid="audit-next" className="h-9 px-4 rounded-lg text-sm bg-zinc-900 border border-zinc-800 disabled:opacity-40">Suivant</button>
          </div>
        </>
      )}
    </div>
  );
}
